// src/components/PuzzleScene.tsx
import React, { useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import {
  smoothstep01,
  CAMERA_POS,
  CAMERA_LOOKAT,
  SCENE_Y_OFFSET,
  SCENE_FADE_IN_DURATION,
  SCENE_BG_COLOR,
  AMBIENT_BASE,
  AMBIENT_HIGHLIGHT,
  DIR_INTENSITY_BASE,
  DIR_INTENSITY_HIGHLIGHT,
  DIR_POS_INITIAL,
  DIR_POS_BASE,
  DIR_POS_HIGHLIGHT,
  FILL_POS,
  FILL_INTENSITY_BASE,
  LIGHT_SPEED_HIGHLIGHT,
  LIGHT_SPEED_FADE,
} from "./puzzleConfig";
import { PuzzlePiece } from "./PuzzlePiece";
import { PuzzleSocket } from "./PuzzleSocket";

type PuzzleSceneProps = {
  solved: boolean;
  solving: boolean;
  onProximityChange: (value01: number) => void;
  onSolveBegin: () => void;
  onSolved: () => void;
};

export function PuzzleScene({
  solved,
  solving,
  onProximityChange,
  onSolveBegin,
  onSolved,
}: PuzzleSceneProps) {
  const { camera } = useThree();

  const ambientRef = useRef<THREE.AmbientLight | null>(null);
  const dirRef = useRef<THREE.DirectionalLight | null>(null);
  const fillRef = useRef<THREE.DirectionalLight | null>(null);

  const tHighlight = useRef(0);
  const tFade = useRef(0);

  // Scene fade-in (prevents any flash on load)
  const sceneFadeIn = useRef(0);

  // Static camera — set once, no animation
  const cameraInitialized = useRef(false);

  useFrame((_, delta) => {
    const ambient = ambientRef.current;
    const dir = dirRef.current;
    if (!ambient || !dir) return;

    if (!cameraInitialized.current) {
      camera.position.set(...CAMERA_POS);
      camera.lookAt(new THREE.Vector3(...CAMERA_LOOKAT));
      cameraInitialized.current = true;
    }

    const highlightTarget = solving ? 1 : 0;
    tHighlight.current = THREE.MathUtils.lerp(
      tHighlight.current,
      highlightTarget,
      1 - Math.exp(-LIGHT_SPEED_HIGHLIGHT * delta)
    );

    const fadeTarget = solved ? 1 : 0;
    tFade.current = THREE.MathUtils.lerp(
      tFade.current,
      fadeTarget,
      1 - Math.exp(-LIGHT_SPEED_FADE * delta)
    );

    const h = smoothstep01(tHighlight.current);
    const f = smoothstep01(tFade.current);

    const litAmbient = THREE.MathUtils.lerp(AMBIENT_BASE, AMBIENT_HIGHLIGHT, h);
    const litDir = THREE.MathUtils.lerp(DIR_INTENSITY_BASE, DIR_INTENSITY_HIGHLIGHT, h);

    const fadeFactor = 1 - f;

    // Scene fade-in from black
    sceneFadeIn.current = Math.min(sceneFadeIn.current + delta / SCENE_FADE_IN_DURATION, 1);
    const fadeIn = sceneFadeIn.current;

    ambient.intensity = litAmbient * fadeFactor * fadeIn;
    dir.intensity = litDir * fadeFactor * fadeIn;
    if (fillRef.current) {
      fillRef.current.intensity = FILL_INTENSITY_BASE * fadeIn;
    }
    dir.position.lerpVectors(DIR_POS_BASE, DIR_POS_HIGHLIGHT, h);
  });

  return (
    <>
      <color attach="background" args={[SCENE_BG_COLOR]} />

      <ambientLight ref={ambientRef} intensity={0} />
      <directionalLight
        ref={dirRef}
        position={DIR_POS_INITIAL}
        intensity={0}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <directionalLight ref={fillRef} position={FILL_POS} intensity={0} />

      <group position={[0, SCENE_Y_OFFSET, 0]}>
        <PuzzleSocket />

        <PuzzlePiece
          onProximityChange={onProximityChange}
          onSolved={onSolved}
          onSolveBegin={onSolveBegin}
        />
      </group>
    </>
  );
}
